import { vacationPackageIncludedBaggageDisplayLines } from "./resolveVacationPackageIncludedBaggage";

function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return toArray(parsed);
    } catch {
      return [];
    }
  }
  if (typeof value === "object") return Object.values(value);
  return [];
}

function toSupplementRows(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((row) => row && typeof row === "object");
  if (typeof value === "object") return Object.values(value).filter((row) => row && typeof row === "object");
  return [];
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatIls(value) {
  const n = toNumber(value);
  return `₪${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function pickFirstNumber(candidates) {
  for (const value of candidates) {
    const n = toNumber(value);
    if (n > 0) return n;
  }
  return 0;
}

function hasOwn(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

function isTrueFlag(value) {
  if (value === true) return true;
  if (typeof value === "number") return value > 0;
  if (typeof value !== "string") return false;
  const t = value.trim().toLowerCase();
  return t === "true" || t === "1" || t === "yes";
}

function classifySupplementRow(row) {
  const type = String(row?.type || row?.category || "")
    .trim()
    .toUpperCase();
  const desc = String(
    row?.description || row?.desc || row?.name || row?.title || ""
  )
    .trim()
    .toUpperCase();
  if (type.includes("TROLLEY") || desc.includes("TROLLEY")) return "trolley";
  if (
    type.includes("CARRY") ||
    desc.includes("CARRY") ||
    desc.includes("HAND BAG") ||
    desc.includes("CABIN")
  ) {
    return "carryOn";
  }
  if (
    type.includes("LUGGAGE") ||
    type.includes("BAGGAGE") ||
    desc.includes("LUGGAGE") ||
    desc.includes("BAGGAGE")
  ) {
    return "checkedBag";
  }
  return null;
}

function resolveDirectionKey(row) {
  const fltRef = String(row?.fltRef ?? row?.flight?.fltRef ?? "").trim();
  const text = String(
    row?.description || row?.desc || row?.name || row?.title || ""
  )
    .toUpperCase()
    .trim();
  if (
    text.includes("ROUNDTRIP") ||
    text.includes("BOTH") ||
    text.includes("RETURN + DEPARTURE")
  ) {
    return "roundtrip";
  }
  if (
    text.includes("RETURN") ||
    text.includes("INBOUND") ||
    text.includes("חזור")
  ) {
    return "inbound";
  }
  if (
    text.includes("DEPARTURE") ||
    text.includes("OUTBOUND") ||
    text.includes("הלוך")
  ) {
    return "outbound";
  }
  if (fltRef === "1") return "outbound";
  if (fltRef === "2") return "inbound";
  if (fltRef === "-3") return "roundtrip";
  return "";
}

function directionLabelHe(directionKey) {
  if (directionKey === "outbound") return "הלוך";
  if (directionKey === "inbound") return "חזור";
  if (directionKey === "roundtrip") return "הלוך חזור";
  return "";
}

function typeLabelHe(typeKey) {
  if (typeKey === "trolley") return "טרולי";
  if (typeKey === "checkedBag") return "מזוודה";
  if (typeKey === "carryOn") return "כבודת יד";
  return "כבודה";
}

function rowWeightLabel(row) {
  const weightText = String(row?.weightText || "").trim();
  if (weightText) return weightText.replace(/\s+/g, " ");
  const params = toArray(row?.params);
  const weight = params.find((p) => String(p?.name || "").toLowerCase() === "weight")?.value;
  const unit = params.find((p) => String(p?.name || "").toLowerCase() === "unit")?.value;
  if (weight != null && String(weight).trim() !== "") {
    return `${String(weight).trim()}${unit ? String(unit).trim() : "KG"}`;
  }
  return "";
}

function parseIncludedLinesFromClientRemarks(rows) {
  const lines = [];
  rows.forEach(({ row }) => {
    const raw = String(row?.clientRemarks || "").trim();
    if (!raw) return;
    raw
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const upper = line.toUpperCase();
        if (!upper.includes("INCLUDED")) return;
        const type = classifySupplementRow({ type: upper, description: upper });
        if (!type) return;
        const direction = resolveDirectionKey({ description: upper });
        const weightMatch = upper.match(/(\d+\s*KG)/);
        const typeHe = typeLabelHe(type);
        const dirHe = directionLabelHe(direction);
        lines.push(
          `${typeHe}${dirHe ? ` ${dirHe}` : ""}${
            weightMatch ? ` ${weightMatch[1].replace(/\s+/g, "")}` : ""
          } כלול`
        );
      });
  });
  return lines;
}

function parseIncludedLinesFromSupplements(rows) {
  const lines = [];
  rows.forEach(({ row, type }) => {
    const includeFlag = isTrueFlag(row?.include) || String(row?.include || "").trim() === "1";
    const fare = toNumber(row?.ourFare ?? row?.fare ?? 0);
    if (!includeFlag && fare > 0) return;
    const typeHe = typeLabelHe(type);
    const dirHe = directionLabelHe(resolveDirectionKey(row));
    const weight = rowWeightLabel(row);
    lines.push(
      `${typeHe}${dirHe ? ` ${dirHe}` : ""}${weight ? ` ${weight}` : ""} כלול`
    );
  });
  return lines;
}

function explicitSelectedCount(row) {
  if (!row || typeof row !== "object") return 0;
  const numericFields = [
    "selectedQuantity",
    "selectedQty",
    "selectedCount",
    "quantity",
    "qty",
    "count",
  ];
  for (const key of numericFields) {
    if (!hasOwn(row, key)) continue;
    const n = Math.floor(toNumber(row[key]));
    if (n > 0) return n;
  }
  return 0;
}

function isExplicitlySelected(row) {
  if (!row || typeof row !== "object") return false;
  if (
    isTrueFlag(row.selected) ||
    isTrueFlag(row.checked) ||
    isTrueFlag(row.isSelected)
  ) {
    return true;
  }
  return explicitSelectedCount(row) > 0;
}

function normalizeRows(orderData, packageData) {
  return [...toArray(orderData?.supplement), ...toArray(packageData?.supplement)]
    .filter((row) => row && typeof row === "object")
    .map((row) => {
      const type = classifySupplementRow(row);
      return {
        row,
        type,
        key: [
          String(row?.id ?? "").trim(),
          String(row?.type ?? "").trim(),
          String(row?.description ?? row?.desc ?? row?.name ?? "").trim(),
          String(row?.fltRef ?? "").trim(),
        ].join("|"),
      };
    })
    .filter((entry) => entry.type);
}

function aggregateCounts(rows) {
  const counts = { checkedBagsCount: 0, trolleyCount: 0, carryOnCount: 0 };
  rows.forEach(({ type, row }) => {
    const count = explicitSelectedCount(row) || 1;
    if (type === "checkedBag") counts.checkedBagsCount += count;
    if (type === "trolley") counts.trolleyCount += count;
    if (type === "carryOn") counts.carryOnCount += count;
  });
  return counts;
}

function selectedRowTotal(row, count) {
  const explicitTotal = pickFirstNumber([
    row?.selectedTotalFare,
    row?.selectedTotal,
    row?.totalFare,
    row?.totalPrice,
    row?.amountTotal,
  ]);
  if (explicitTotal > 0) return explicitTotal;
  const unitFare = toNumber(row?.ourFare ?? row?.fare ?? 0);
  if (count > 1 && hasOwn(row, "quantity")) return unitFare * count;
  if (count > 1 && hasOwn(row, "qty")) return unitFare * count;
  if (count > 1 && hasOwn(row, "selectedQuantity")) return unitFare * count;
  return unitFare;
}

function buildSelectedPaidLines(selectedRows) {
  const grouped = new Map();
  selectedRows.forEach(({ type, row }) => {
    const direction = resolveDirectionKey(row);
    const count = explicitSelectedCount(row) || 1;
    const lineTotal = selectedRowTotal(row, count);
    const key = `${type}|${direction}|${toNumber(row?.ourFare ?? row?.fare ?? 0)}`;
    const cur = grouped.get(key) || {
      type,
      direction,
      count: 0,
      total: 0,
    };
    cur.count += count;
    cur.total += lineTotal;
    grouped.set(key, cur);
  });
  const lines = [];
  let total = 0;
  grouped.forEach((entry) => {
    const typeHe = typeLabelHe(entry.type);
    const dirHe = directionLabelHe(entry.direction);
    const countPrefix = entry.count > 1 ? `${entry.count}× ` : "";
    const amountText = entry.total > 0 ? ` — ${formatIls(entry.total)}` : "";
    lines.push(`${countPrefix}${typeHe}${dirHe ? ` ${dirHe}` : ""}${amountText}`);
    total += entry.total;
  });
  return { lines, total };
}

function dedupLines(lines) {
  const seen = new Set();
  const out = [];
  lines.forEach((line) => {
    const t = String(line || "").trim();
    if (!t) return;
    if (seen.has(t)) return;
    seen.add(t);
    out.push(t);
  });
  return out;
}

/**
 * Unified baggage summary for all package summary views.
 */
function rowIdentityKey(row) {
  return [
    String(row?.id ?? "").trim(),
    String(row?.type ?? "").trim(),
    String(row?.description ?? row?.desc ?? row?.name ?? "").trim(),
    String(row?.fltRef ?? row?.flight?.fltRef ?? "").trim(),
    String(row?.paxRef ?? row?.pax?.["@seqNum"] ?? "").trim(),
    String(row?.["@seqNum"] ?? "").trim(),
  ].join("|");
}

function normalizeRowsFromSource(rows) {
  return rows
    .filter((row) => row && typeof row === "object")
    .map((row) => ({
      row,
      type: classifySupplementRow(row),
      key: rowIdentityKey(row),
    }))
    .filter((entry) => entry.type);
}

export function resolveBaggageSummary(
  orderData,
  packageData,
  roomsAndPaxInfo,
  selectedSupplementsState = null
) {
  const selectedStateRows = toSupplementRows(selectedSupplementsState);
  let normalizedRows = selectedStateRows.length
    ? normalizeRowsFromSource(selectedStateRows)
    : normalizeRows(orderData, packageData);
  // Live catalog can re-init with checked:false and shadow DB selection — fall back to order.
  if (selectedStateRows.length) {
    const stateSelected = normalizedRows.filter(({ row }) =>
      isExplicitlySelected(row)
    );
    if (stateSelected.length === 0) {
      const fromOrder = normalizeRows(orderData, packageData);
      if (fromOrder.some(({ row }) => isExplicitlySelected(row))) {
        normalizedRows = fromOrder;
      }
    }
  }
  const availableMap = new Map();
  normalizedRows.forEach((entry) => {
    if (!availableMap.has(entry.key)) availableMap.set(entry.key, entry);
  });
  const availableRows = [...availableMap.values()];
  const selectedRows = availableRows.filter(({ row }) => isExplicitlySelected(row));

  const availableCounts = aggregateCounts(availableRows);
  const selectedCounts = aggregateCounts(selectedRows);
  const selectedPaid = buildSelectedPaidLines(selectedRows);
  const totalBaggagePrice = selectedPaid.total;
  const selectedPaidLines = selectedPaid.lines;
  const selectedPaidTotal = selectedPaid.total;
  const lf = packageData?.linkedFlights;
  const lfLooksVpLinked =
    lf &&
    typeof lf === "object" &&
    Boolean(lf.flight || (Array.isArray(lf) && lf.length > 0));
  const vpFlightIncludedLines = lfLooksVpLinked
    ? vacationPackageIncludedBaggageDisplayLines(lf)
    : [];
  const includedLines = dedupLines([
    ...vpFlightIncludedLines,
    ...parseIncludedLinesFromClientRemarks([...toArray(orderData?.extended), ...toArray(packageData?.extended)].map((row) => ({
      row,
      type: null,
    }))),
    ...parseIncludedLinesFromSupplements(availableRows),
  ]);
  const baggagePriceFromSnapshot = pickFirstNumber([
    orderData?.BaggageFee,
    orderData?.baggageFee,
    orderData?.OurFareSum,
    packageData?.BaggageFee,
    packageData?.baggageFee,
    packageData?.OurFareSum,
  ]);
  const isIncludedInTotalPrice = selectedPaidTotal > 0;
  const labelLines = [...includedLines, ...selectedPaidLines];
  const hasBaggage = labelLines.length > 0;

  return {
    hasBaggage,
    checkedBagsCount: selectedCounts.checkedBagsCount,
    trolleyCount: selectedCounts.trolleyCount,
    carryOnCount: selectedCounts.carryOnCount,
    totalBaggagePrice,
    selectedPaidTotal,
    selectedPaidLines,
    includedLines,
    isIncludedInTotalPrice,
    baggagePriceFromSnapshot,
    availableBaggageOptionsCount: availableRows.length,
    selectedBaggageItemsCount: selectedRows.length,
    availableCounts,
    labelLines,
    roomsCount: Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo.length : 0,
    // Backward-compatible fields for existing renderers.
    items: labelLines,
  };
}

